import { produce } from "immer";
import {
  difference,
  each,
  filter,
  get,
  head,
  includes,
  isEqual,
  isNumber,
  isObject,
  isString,
  map,
  set,
  size,
  uniq,
} from "lodash";
import { Country } from "@linkfluence/models/country";
import { AbstractDate } from "@linkfluence/models/date";
import { Gender } from "@linkfluence/models/gender";
import { Metric } from "@linkfluence/models/metric";
import {
  Platform,
  WebPlatform,
  getPlatformColor,
  isOfflineMediaPlatform,
  isWebPlatform,
} from "@linkfluence/models/platform";
import { Scene } from "@linkfluence/models/scene";
import { Tone } from "@linkfluence/models/tone";
import { getUrl } from "@tootools/http-utils";
import { getVideoThumbnail } from "@tootools/social";
import { Project } from "./project";

type UserGender = Gender.Male | Gender.Female;

function hasReply(inReplyToStatusId: string | number) {
  if (isNumber(inReplyToStatusId)) {
    return (
      inReplyToStatusId &&
      inReplyToStatusId !== null &&
      inReplyToStatusId !== -1
    );
  } else if (isString(inReplyToStatusId)) {
    return inReplyToStatusId !== "" && inReplyToStatusId !== "-1";
  } else {
    return false;
  }
}

export function getPostSortFromMetric(
  metric: Metric
): "date" | Metric.Engagement | Metric.Reach | Metric.Impression {
  if (includes([Metric.Engagement, Metric.Reach, Metric.Impression], metric)) {
    return metric as Metric.Engagement | Metric.Reach | Metric.Impression;
  } else {
    return "date";
  }
}

export const isTikTokPost = (post: Post): post is TikTokPost =>
  post.origin.platform === Platform.Tiktok;

interface Geolocation {
  admin1: string;
  cid: string;
  country: string;
  location: {
    lon: number;
    lat: number;
  };
}

interface Geo {
  given: Geolocation;
  inferred: Geolocation;
}

interface Media {
  video: Array<{
    url: string;
    thumbnail: string;
  }>;
  image: string[]; // @TODO
  presentation: any[]; // @TODO
  meta: {
    logo: any[]; // @TODO
    object: any[]; // @TODO
    scenes: Scene[];
    person: {
      count: number;
      category: string; // @TODO
      gender: null; // @TODO
      "age-group": null; // @TODO
      "person-identification": any[]; // @TODO
    };
  };
}
interface Attachment {
  name: string;
  description: string;
  thumbnail: string;
  type: any; // @TODO
  url: string;
}

interface WebUrl {
  reversed: string;
  normalized: string;
  terminal: string;
  site: {
    reversed: string;
    url: string;
    siteType: string;
  };
}

// Generic
interface GenericPost<P extends Platform, T extends string = P> {
  uid: string;
  type: T;
  permalink: string;
  date: string;
  origin: {
    source: string;
    detailed: string;
    platform: P;
  };
  // Radar
  focuses: number[];
  radar: {
    favorite: boolean;
    trash: boolean;
    created: string;
    updated: string;
    userUpdated: string;
    tag: {
      user: string[]; // @TODO
      custom: Record<string, string[]>;
      focus: number[];
    };
  };
  // Content
  text: string;
  title?: string;
  highlights: {
    text: string;
    title?: string;
    "photo-mentions"?: string;
  };
  // Attachments
  media?: Media;
  // Score
  engagementScore?: number;
  reach?: number;
  impression?: number;
  // Meta
  affects: string[];
  emotion: string[]; // @TODO
  geo: Geo;
  lang: string;
  tone: Tone;
  category?: string; // @TODO
  keyword: string[]; // @TODO
  contentClassification: string[];
}

// Web

interface GenericWebPost<
  P extends WebPlatform,
  T extends "web" | "forum" = "web"
> extends GenericPost<P, T> {
  // Source
  url: WebUrl;
  // List of named entities
  namedEntity?: {
    location: string[];
    person: string[];
    organization: string[];
    misc: string[];
  };
  pageRank: number;
  story?: string;
}

export type WebsitePost = GenericWebPost<typeof Platform.Website>;

export type MediaPost = GenericWebPost<typeof Platform.Media>;

export type BlogPost = GenericWebPost<typeof Platform.Blog>;

export type CommentPost = GenericWebPost<typeof Platform.Comment>;

export interface ForumPost
  extends GenericWebPost<typeof Platform.Forum, "forum"> {
  forumName: string;
  postNum: number;
  topicUrl: string;
  score: any; // @TODO
  allowframe: boolean;
}

// Offline Media

interface GenericOfflineMedia<P extends Platform> extends GenericPost<P, P> {
  offlineMedia: {
    name: string;
    profileImg: string;
    id: string;
    idStr: string;
    potentialImpression: number;
    type: P;
  };
  broadcastMeta?: {
    id: string;
    duration: number;
    program: string;
    presenter: string;
  };
}

export type RadioPost = GenericOfflineMedia<typeof Platform.Radio>;

export type TelevisionPost = GenericOfflineMedia<typeof Platform.Television>;

export interface PressPost extends GenericOfflineMedia<typeof Platform.Press> {
  teaser: any; // @TODO
  pdfUrl: string;
  user: {
    name: string;
  };
}

type Mention = {
  id: number;
  idStr: string;
  name: string;
  screenName: string;
};

// Social Post
interface GenericSocialPost<P extends Platform> extends GenericPost<P, P> {
  score?: Record<string, number>;
  hashtag?: string[];
  mentions?: Mention[];
}

export interface TikTokPost extends GenericSocialPost<typeof Platform.Tiktok> {
  user: {
    birthDate: string;
    followers: number;
    gender: UserGender;
    geo: Geo;
    id: string;
    idStr: string;
    name: string;
    screenName: string;
    profileImg: string;
  };
}

export interface DouyinPost extends GenericSocialPost<typeof Platform.Douyin> {
  user: {
    name: string;
    geo: Geo;
    gender: UserGender;
    birthDate: string;
  };
  score: {
    comment: number;
    like: number;
    share: number;
    view: number;
  };
}

export interface TwitchPost extends GenericSocialPost<typeof Platform.Twitch> {
  user: {
    id: number;
    name: string;
    screenName: string;
    gender: UserGender;
    followers: number;
    views: number;
    profileImg: string;
  };
}

export interface PinterestPost
  extends GenericSocialPost<typeof Platform.Pinterest> {
  user: {
    id: number;
    name: string;
    screenName: string;
    gender: UserGender;
    followers: number;
    url: string;
    profileImg: string;
  };
  score: {
    save: number;
    done: number;
  };
}

export interface YoukuPost extends GenericSocialPost<typeof Platform.Youku> {
  user: {
    id: string;
    idStr: string;
    name: string;
    gender: UserGender;
    birthDate: string;
  };
  score: {
    comment: number;
    like: number;
    dislike: number;
    favorite: number;
    view: number;
  };
}

export interface RedditPost extends GenericSocialPost<typeof Platform.Reddit> {
  user: {
    gender: UserGender;
    id: string;
    name: string;
    profileImg: string;
    screenName: string;
    karma: number;
    awardeeKarma: number;
    awarderKarma: number;
  };
  radar: GenericSocialPost<"reddit">["radar"] & {
    korpuz?: number[];
    focus?: number[];
  };
  community: {
    id: string;
    "screen-name": string;
    subscriber: number;
    title: string;
  };
  score: {
    comment: number;
    up: number;
    "upvote-ratio": number;
  };
  inReplyToStatusId: number;
  inReplyToStatusIdStr: string;
}

export interface YoutubePost
  extends GenericSocialPost<typeof Platform.Youtube> {
  user: {
    id: string;
    idStr: string;
    name: string;
    gender: UserGender;
    birthDate: string;
  };
  score: {
    twitter: number;
    facebook: number;
    link: number;
    comment: number;
    like: number;
    dislike: number;
    view: number;
  };
}

export interface FacebookPost
  extends GenericSocialPost<typeof Platform.Facebook> {
  caption: string;
  user: {
    id: string;
    idStr: string;
    name: string;
    profileImg: string;
    geo: Geo;
    gender: UserGender;
    type: "page" | "person";
    fans: number;
    birthDate: string;
    verified: boolean;
    anonymous: boolean;
    screenName: string;
  };
  url: {
    reversed: string;
    normalized: string;
    terminal: string;
  };
  score: {
    share: number;
    like: number;
    comment: number;
    impressionTotal: number;
    impressionUnpaid: number;
    impressionPaid: number;
  };
  attachment: Attachment;
  id: string;
  isHidden: boolean;
  isDeleted: boolean;
  toFBPage: any; // @TODO
  inReplyToStatusId: string;
}
export interface LinkedinPost
  extends GenericSocialPost<typeof Platform.Linkedin> {
  attachment: Attachment;
  id: string;
  inReplyToStatusId: string;
  keyphrase: {
    display: string[];
    normalized: string[];
    keyword: string[];
  };
  score: {
    share: number;
    like: number;
    comment: number;
    impressionTotal: number;
    impressionUnpaid: number;
    impressionPaid: number;
  };
  toCompanyId: string;
  typeOfPublication: any; // @TODO
  url: {
    reversed: string;
    normalized: string;
    terminal: string;
  };
  user: {
    id: string;
    idStr: string;
    name: string;
    profileImg: string;
    geo: Geo;
    gender: UserGender;
    type: "page" | "person";
    fans: number;
    birthDate: string;
    verified: boolean;
    anonymous: boolean;
    screenName: string;
  };
}

export interface TwitterPost
  extends GenericSocialPost<typeof Platform.Twitter> {
  score: { rt: number; favourite: number; reply: number };
  inReplyToStatusId: number;
  inReplyToStatusIdStr: string;
  isRT: boolean;
  id: number;
  idStr: string;
  externalUrl: string;
  retweet: any; // @TODO
  user: {
    id: string;
    idStr: string;
    name: string;
    geo: Geo;
    screenName: string;
    birthDate: string;
    profileImg: string;
    verified: boolean;
    gender: UserGender;
    followers: number;
  };
  quote?: Quote;
}

export interface Quote {
  id?: number;
  date?: string;
  user?: {
    id?: number;
    screenName?: string;
    name?: string;
    profileImg?: string;
    verified?: boolean;
  };
  idStr?: string;
  score?: {
    rt?: number;
    favourite?: number;
    reply?: number;
  };
  media?: {
    video?: string[];
    image?: string[];
    presentation?: any[];
    meta?: any;
    embed?: any[];
  };
  text?: string;
  permalink?: string;
}

export interface InstagramPost
  extends GenericSocialPost<typeof Platform.Instagram> {
  user: {
    id: string;
    idStr: string;
    name: string;
    screenName: string;
    gender: UserGender;
    profileImg: string;
    followers: number;
    verified: boolean;
    birthDate: string;
    businessId: number;
    accountType: "business" | "undetected";
    anonymous: boolean;
  };
  score: {
    like: number;
    comment: number;
    reach: number;
    saved: number;
    videoView: number;
    impression: number;
    story: number;
  };
  id: string;
  isStory: boolean;
  inReplyToStatusId: number;
  inReplyToStatusIdStr: string;
  toIgAccount: number;
  businessId: number;
  businessIdStr: string;
}

export interface VKontaktePost
  extends GenericSocialPost<typeof Platform.Vkontakte> {
  inReplyToStatusIdStr: string;
  user: {
    id: string;
    idStr: string;
    name: string;
    screenName: string;
    gender: UserGender;
    type: "page" | "person";
    followers: number;
    verified: boolean;
    birthDate: string;
    profileImg: string;
  };
  score: { repost: number; like: number; comment: number };
}

export interface WechatPost extends GenericSocialPost<typeof Platform.Wechat> {
  user: {
    id: string;
    idStr: string;
    screenName: string;
    gender: UserGender;
    name: string;
  };
  score: {
    like: number;
    read: number;
  };
}

export interface SinaweiboPost
  extends GenericSocialPost<typeof Platform.Sinaweibo> {
  user: {
    id: string;
    idStr: string;
    screenName: string;
    gender: UserGender;
    name: string;
    followers: number;
    profileImg: string;
    geo: Geo;
    verified: boolean;
    birthDate: string;
  };
  inReplyToStatusId: number;
  inReplyToStatusIdStr: string;
  id: number;
  idStr: string;
  retweet: any; // @TODO
  isRT: boolean;
  externalUrl: any; // @TODO,
  score: {
    rt: number;
    favourite: number;
    reply: number;
  };
}

export interface LittleRedBookPost
  extends GenericSocialPost<typeof Platform.LittleRedBook> {
  title: string;
  user: {
    id: string;
    name: string;
    geo: Geo;
    gender: UserGender;
    birthDate: string;
  };
  score: { comment: number; like: number; star: number };
}

export interface BilibiliPost
  extends GenericSocialPost<typeof Platform.Bilibili> {
  user: {
    birthDate: string;
    gender: UserGender;
    geo: Geo;
    id: string;
    idStr: string;
    name: string;
    profileImg: string;
    screenName: string;
  };
  score: {
    comment: number;
    danmaku: number;
    dislike: number;
    favorite: number;
    like: number;
    share: number;
    view: number;
  };
}

export interface ReviewPost extends GenericSocialPost<typeof Platform.Review> {
  title: string;
  url: WebUrl;
  score: { raw: number; normalized: number };
  product: {
    name: string;
    "product-range": any; // @TODO
    ean: any; // @TODO
  };
  productUrl: string;
  user: {
    name: string;
    geo: Geo;
    gender: UserGender;
  };
}

export interface DailymotionPost
  extends GenericSocialPost<typeof Platform.Dailymotion> {
  user: {
    id: string;
    idStr: string;
    name: string;
    gender: UserGender;
    profileImg: string;
    nbVideo: number;
    views: number;
    birthDate: string;
  };
  score: {
    views: number;
    comment: number;
  };
}

export interface GplusPost extends GenericSocialPost<typeof Platform.Gplus> {
  user: {
    id: string;
    idStr: string;
    name: string;
    gender: UserGender;
    profileImg: string;
    birthDate: string;
  };
}

export interface ClusterPost {
  author: string;
  date: string;
  engagementScore: number;
  platform: Platform;
  reach: number;
  uid: string;
  userId: string;
  postId: string;
  mediaUrl: string;
  permalink: string;
  srcFallback?: string;
}

export type SocialPost =
  | TwitterPost
  | FacebookPost
  | InstagramPost
  | VKontaktePost
  | WechatPost
  | SinaweiboPost
  | YoukuPost
  | LittleRedBookPost
  | ReviewPost
  | DailymotionPost
  | GplusPost
  | YoutubePost
  | TwitchPost
  | TikTokPost
  | DouyinPost
  | PinterestPost
  | RedditPost
  | LinkedinPost
  | BilibiliPost;

type OfflinePost = RadioPost | TelevisionPost | PressPost;

type WebPost = WebsitePost | MediaPost | BlogPost | ForumPost | CommentPost;

export type Post = SocialPost | WebPost | OfflinePost;

export function getPlatformFromPost(post: Post): Platform {
  return post.origin.platform;
}

type PostPlatforms = {
  [Platform.Twitter]: TwitterPost;
  [Platform.Facebook]: FacebookPost;
  [Platform.Vkontakte]: VKontaktePost;
  [Platform.Instagram]: InstagramPost;
  [Platform.Review]: ReviewPost;
  [Platform.Dailymotion]: DailymotionPost;
  [Platform.Gplus]: GplusPost;
  [Platform.Youtube]: YoutubePost;
  [Platform.Twitch]: TwitchPost;
  [Platform.Pinterest]: PinterestPost;
  [Platform.Reddit]: RedditPost;
  [Platform.Linkedin]: LinkedinPost;
  // China
  [Platform.Tiktok]: TikTokPost;
  [Platform.Douyin]: DouyinPost;
  [Platform.Sinaweibo]: SinaweiboPost;
  [Platform.Wechat]: WechatPost;
  [Platform.Youku]: YoukuPost;
  [Platform.LittleRedBook]: LittleRedBookPost;
  [Platform.Bilibili]: BilibiliPost;
  // Web
  [Platform.Website]: WebsitePost;
  [Platform.Media]: MediaPost;
  [Platform.Blog]: BlogPost;
  [Platform.Comment]: CommentPost;
  [Platform.Forum]: ForumPost;
  // Offline Media
  [Platform.Radio]: RadioPost;
  [Platform.Television]: TelevisionPost;
  [Platform.Press]: PressPost;
};

export interface PostAuthor {
  id: string;
  name: string;
  screenName: string;
  permalink: string;
  profileImg: string;
  platform: Platform;
}

type PostMetrics = {
  rating?: number;
  reach?: number;
  engagement?: number;
};

export class PostLens<P extends Post> {
  post: P;
  constructor(post: P) {
    this.post = post;
  }
  private castHttpPicture = (src: string) => {
    if (src.indexOf("sinaimg.cn") !== -1) {
      return src.replace("https://", "http://");
    } else {
      return src;
    }
  };
  private getWebsiteAuthorName = (post: WebsitePost) => {
    return ("" + (post.url.site.url || ""))
      .replace("http://", "")
      .replace("https://", "");
  };
  public get() {
    return this.post;
  }
  public getAuthorAvatar() {
    return "";
  }
  public getHashtags() {
    if (PostLens.isSocial(this.post)) {
      return this.post.hashtag;
    } else {
      return [];
    }
  }
  public getAuthorName() {
    if (PostLens.isSocial(this.post)) {
      return this?.post?.user?.name ?? null;
    } else if (PostLens.isWeb(this.post)) {
      return this.getWebsiteAuthorName(this.post as WebsitePost);
    } else if (PostLens.isOfflineMedia(this.post)) {
      return this.post.offlineMedia.name;
    }
    return null;
  }
  public getAuthorId() {
    return null;
  }
  public getInfluencerUid() {
    return null;
  }
  public getAuthorPermalink() {
    if (PostLens.isWeb(this.post)) {
      return this.post.url.site.url;
    }
    return null;
  }
  public getAuthorScreenName() {
    return null;
  }
  //Use with { getCoverFallback } from "./picture" to avoid missing images
  public getCover() {
    const post = this.post;
    const hasMedia = this.hasMedia();
    function _getCover() {
      if (hasMedia) {
        const videos = post.media.video;
        const images = post.media.image;
        if (size(images)) {
          return head(images);
        } else if (size(videos)) {
          const video = head(videos);
          return getVideoThumbnail(video.url, video.thumbnail);
        } else {
          return "";
        }
      }
      return "";
    }
    return this.castHttpPicture(_getCover());
  }
  public getLargePreviewData() {
    return null;
  }
  public getMetrics(): PostMetrics {
    // SUPPORTLKF-1091 No metrics for Linkedin
    if (this.getPlatform() === Platform.Linkedin) {
      return { reach: null, engagement: null };
    }

    return {
      reach: this.post.reach === 0 ? null : this.post.reach,
      engagement:
        this.post.engagementScore === 0 ? null : this.post.engagementScore,
    };
  }
  public getPermalink() {
    return this.post.permalink;
  }
  public getPhotoMentions() {
    return null;
  }
  public getPlatform() {
    const type = this.post.type;
    const platform = this.post.origin.platform;
    return type !== platform || !type ? platform : type;
  }
  public getPostAuthor(): PostAuthor {
    return {
      platform: this.getPlatform(),
      id: "" + this.getAuthorId(), // @TODO
      name: this.getAuthorName(),
      screenName: this.getAuthorScreenName(),
      permalink: this.getAuthorPermalink(),
      profileImg: this.getAuthorAvatar(),
    };
  }
  public getPostAuthorName() {
    return this.getAuthorName();
  }

  public getScores(): Record<string, number> {
    return PostLens.isSocial(this.post) ? this.post.score : null;
  }
  public getText(highlight: boolean = true) {
    const { highlights, text } = this.post;
    if (highlight && highlights && highlights.text) return highlights.text;
    return text;
  }
  public getTitle(highlight: boolean = true) {
    const { highlights, title } = this.post;
    if (highlight && highlights && highlights.title) return highlights.title;
    return title;
  }
  public isAuthorVerified() {
    return null;
  }
  public hasContext() {
    return null;
  }
  public hasTitle() {
    return !!this.post.title;
  }
  public hasMedia() {
    return !!this.post.media;
  }
  public set(post: P) {
    this.post = post;
  }
  static isOfflineMedia(post: Post): post is OfflinePost {
    return isOfflineMediaPlatform(getPlatformFromPost(post));
  }
  static isPlatform<V extends PostPlatforms, K extends keyof PostPlatforms>(
    platform: K,
    post: Post
  ): post is V[K] {
    return getPlatformFromPost(post) === platform;
  }
  static isSocial(post: Post): post is SocialPost {
    return !PostLens.isWeb(post) && !PostLens.isOfflineMedia(post);
  }
  static isWeb(post: Post): post is WebPost {
    return isWebPlatform(getPlatformFromPost(post));
  }
}

class LensBlogMediaWebsite extends PostLens<
  WebsitePost | BlogPost | MediaPost
> {
  public getInfluencerUid() {
    return this.post.url.site.reversed;
  }
  public getAuthorAvatar() {
    return `https://magnum.linkfluence.com/photo?url=${this.post.url.site.url}&size=small`;
  }
}

class LensCommentForum extends PostLens<ForumPost | CommentPost> {
  public getInfluencerUid() {
    return this.post.url.site.reversed;
  }
  public getAuthorAvatar() {
    return `https://magnum.linkfluence.com/photo?url=${this.post.url.site.url}&size=small`;
  }
  public getMetrics() {
    return {};
  }
}

class LensDailymotion extends PostLens<DailymotionPost> {
  public getAuthorAvatar() {
    return this.post.user.profileImg;
  }
  public getInfluencerUid() {
    return this.post.user.idStr;
  }
  public getMetrics() {
    return { reach: this.post.reach === 0 ? null : this.post.reach };
  }
}

class LensFacebook extends PostLens<FacebookPost> {
  private getAttachmentLink = (post: FacebookPost) => {
    if (post.url && post.url.normalized) return post.url.normalized;
    return post.permalink;
  };
  public getAuthorAvatar() {
    return this.post.user.profileImg;
  }
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.idStr;
  }
  public getAuthorName() {
    return this.post.user.anonymous ? null : this.post.user.name;
  }
  public getAuthorPermalink() {
    return getUrl("https://facebook.com/:pageId", {
      pageId: this.post.user.idStr,
    });
  }
  public getLargePreviewData() {
    if (this.post.attachment) {
      const link = this.getAttachmentLink(this.post);
      return {
        thumbnail: this.post.attachment.thumbnail,
        caption: this.post.caption,
        name: this.post.attachment.name,
        description: this.post.attachment.description,
        link,
      };
    }
    return null;
  }
  public hasContext() {
    return (
      hasReply(this.post.inReplyToStatusId) && {
        label: `SOCIAL::FACEBOOK::POST::REPLY`,
      }
    );
  }
  public isAuthorVerified() {
    return this.post.user.verified;
  }
}

class LensInstagram extends PostLens<InstagramPost> {
  public getAuthorAvatar() {
    return this.post.user.profileImg;
  }
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.screenName;
  }
  public getAuthorName() {
    return this.post.user.anonymous ? null : this.post.user.screenName;
  }
  public getAuthorPermalink() {
    return this.post.user.screenName
      ? getUrl("http://instagram.com/:screenName", {
          screenName: this.post.user.screenName,
        })
      : null;
  }
  public getAuthorScreenName() {
    return this.post.user.screenName;
  }
  public getPhotoMentions() {
    const photoMentions = get(this.post.highlights, "photo-mentions");
    if (!photoMentions) return null;
    const matchingMentions = filter(photoMentions.split(", "), (mention) =>
      mention.includes("</hl>")
    );
    const mentions = map(matchingMentions, (matchingMention) => {
      const cleanMention = matchingMention.replace(/^@<hl[^>]*>|<\/hl>$/g, "");
      return {
        label: "@" + cleanMention,
        url: "https://www.instagram.com/" + cleanMention.toLowerCase(),
      };
    });
    return {
      mentions,
      color: getPlatformColor(this.post.origin.platform),
    };
  }
  public hasContext() {
    return (
      hasReply(this.post.inReplyToStatusId) && {
        label: `SOCIAL::INSTAGRAM::POST::REPLY`,
      }
    );
  }
  public isAuthorVerified() {
    return this.post.user.verified;
  }
}

class LensPinterest extends PostLens<PinterestPost> {
  public getAuthorAvatar() {
    return this.post.user.profileImg;
  }
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.screenName;
  }
  public getAuthorPermalink() {
    return getUrl("https://www.pinterest.com/:id/", {
      id: this.post.user.screenName,
    });
  }
  public getAuthorScreenName() {
    return this.post.user.screenName;
  }
}

class LensPress extends PostLens<PressPost> {
  public getPermalink() {
    return this.post.pdfUrl;
  }
  public getMetrics() {
    return {};
  }
}

class LensRadioTelevision extends PostLens<RadioPost | TelevisionPost> {
  public getTitle() {
    return this.post.broadcastMeta ? this.post.broadcastMeta.program : null;
  }
  public getMetrics() {
    return {};
  }
}

class LensReddit extends PostLens<RedditPost> {
  public getPostAuthorName() {
    return `u/${this.post.user.screenName}`;
  }
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.id;
  }
  public getAuthorName() {
    return `u/${this.post.user.screenName}`;
  }
  public getAuthorAvatar() {
    if (this.post.user.profileImg) {
      return this.post.user.profileImg;
    }
    return `https://magnum.linkfluence.com/photo?url=http://reddit.com&size=small`;
  }
  public hasContext() {
    return {
      label: hasReply(this.post.inReplyToStatusId)
        ? "SOCIAL::REDDIT::COMMENT_ON"
        : "SOCIAL::REDDIT::POST_ON",
      value: this.post.community
        ? `r/${this.post?.community["screen-name"]}`
        : "",
    };
  }

  public getAuthorPermalink() {
    return getUrl("https://www.reddit.com/u/:id", {
      id: this.post.user.id,
    });
  }
  public getMetrics() {
    return {};
  }
  public getAuthorScreenName() {
    return this.post.user.screenName;
  }
}

class LensReview extends PostLens<ReviewPost> {
  public getAuthorAvatar() {
    return `https://magnum.linkfluence.com/photo?url=${this.post.url.site.url}&size=small`;
  }
  public getAuthorName() {
    return LensReview.isIndividualReviewAuthor(this.post)
      ? this.post.user.name
      : null;
  }
  public getPostAuthorName() {
    const platform = this.post.url.site.url;
    return platform?.split("/")[2];
  }
  public getInfluencerUid() {
    return this.post.url.site.reversed;
  }
  public getMetrics() {
    if (this.post.score && this.post.score.normalized) {
      const rating = Math.round(this.post.score.normalized / 20);
      return { rating: rating === 0 ? null : rating };
    }
    return { rating: null };
  }
  public hasContext() {
    return {
      label: "SOCIAL::REVIEW::POST::PRODUCT",
      value: this.post.product.name,
    };
  }
  static isIndividualReviewAuthor(reviewPost: ReviewPost): boolean {
    const formattedUrl = reviewPost?.url?.site?.url?.split("://")[1] || "";
    return formattedUrl !== reviewPost.user.name;
  }
}

class LensSinaweibo extends PostLens<SinaweiboPost> {
  public getAuthorAvatar() {
    return this.post.user.profileImg;
  }
  public getAuthorName() {
    return this.post.user.screenName;
  }
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.idStr;
  }
  public getAuthorPermalink() {
    return getUrl("http://www.weibo.com/u/:id", { id: this.post.user.id });
  }
  public getAuthorScreenName() {
    return this.post.user.screenName;
  }
  public hasContext() {
    return hasReply(this.post.inReplyToStatusId)
      ? { label: "SOCIAL::SINAWEIBO::POST::REPLY" }
      : this.post.isRT
      ? { label: "SOCIAL::SINAWEIBO::POST::FORWARD" }
      : null;
  }
  public isAuthorVerified() {
    return this.post.user.verified;
  }
}

class LensTiktok extends PostLens<TikTokPost> {
  public getAuthorAvatar() {
    return this.post?.user?.profileImg ?? null;
  }
  public getAuthorId() {
    return this.post?.user?.id ?? null;
  }
  public getInfluencerUid() {
    return this.post?.user?.idStr ?? null;
  }
  public getAuthorPermalink() {
    return this.post?.user?.screenName
      ? getUrl("https://www.tiktok.com/@:screenName", {
          screenName: this.post.user.screenName,
        })
      : null;
  }
  public getAuthorScreenName() {
    return this.post?.user?.screenName ?? null;
  }
}

class LensTwitch extends PostLens<TwitchPost> {
  public getAuthorAvatar() {
    return this.post.user.profileImg;
  }
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.id;
  }
  public getAuthorPermalink() {
    return getUrl("http://www.twitch.tv/:id", {
      id: this.post.user.screenName,
    });
  }
  public getAuthorScreenName() {
    return this.post.user.screenName;
  }
  public getMetrics() {
    return { reach: this.post.reach === 0 ? null : this.post.reach };
  }
}

class LensTwitter extends PostLens<TwitterPost> {
  public getAuthorAvatar() {
    return this.post.user.profileImg;
  }
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.idStr;
  }
  public getAuthorPermalink() {
    return this.post.user.screenName
      ? getUrl("http://twitter.com/:screenName", {
          screenName: this.post.user.screenName,
        })
      : null;
  }
  public getAuthorScreenName() {
    return this.post.user.screenName;
  }
  public hasContext() {
    return hasReply(this.post.inReplyToStatusIdStr)
      ? { label: "SOCIAL::TWITTER::POST::REPLY" }
      : this.post.isRT && { label: "SOCIAL::TWITTER::POST::REPLYCTX" };
  }
  public isAuthorVerified() {
    return this.post.user.verified;
  }
}

class LensVkontakte extends PostLens<VKontaktePost> {
  public getAuthorAvatar() {
    return this.post.user.profileImg;
  }
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.idStr;
  }
  public getAuthorPermalink() {
    return getUrl("http://vk.com/:id", { id: this.post.user.id });
  }
  public getAuthorScreenName() {
    return this.post.user.screenName;
  }
  public hasContext() {
    return (
      hasReply(this.post.inReplyToStatusIdStr) && {
        label: "SOCIAL::VKONTAKTE::POST::REPLY",
      }
    );
  }
  public isAuthorVerified() {
    return this.post.user.verified;
  }
}
class LensWechat extends PostLens<WechatPost> {
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.idStr;
  }
  public getAuthorName() {
    // @TODO Temporary FIX
    return this.post.user.screenName;
  }
  public getAuthorScreenName() {
    return this.post.user.screenName;
  }
}
class LensYoutube extends PostLens<YoutubePost> {
  public getAuthorPermalink() {
    return this.post.user.id
      ? getUrl("http://www.youtube.com/channel/:id", {
          id: this.post.user.id,
        })
      : "http://www.youtube.com";
  }
  public getMetrics() {
    return {};
  }
}

class LensLinkedin extends PostLens<LinkedinPost> {
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.idStr;
  }
  public getAuthorPermalink() {
    return getUrl("http://www.linkedin.com/in/:id", {
      id: this.post.user.id,
    });
  }
}

class LensBilibili extends PostLens<BilibiliPost> {
  public getAuthorPermalink() {
    return getUrl("https://space.bilibili.com/:id", {
      id: this.post.user.idStr,
    });
  }
  public getAuthorId() {
    return this.post.user.id;
  }
  public getInfluencerUid() {
    return this.post.user.idStr;
  }
  public getAuthorAvatar() {
    return this.post.user.profileImg;
  }
}

class LensLittleRedBook extends PostLens<LittleRedBookPost> {
  public getInfluencerUid() {
    return this.post.user.id;
  }
}

class LensGenericPosts extends PostLens<YoukuPost | GplusPost> {
  public getMetrics() {
    return {};
  }
}

const postPlatforms = {
  [Platform.Bilibili]: LensBilibili,
  [Platform.Blog]: LensBlogMediaWebsite,
  [Platform.Comment]: LensCommentForum,
  [Platform.Dailymotion]: LensDailymotion,
  [Platform.Facebook]: LensFacebook,
  [Platform.Feed]: LensGenericPosts,
  [Platform.Forum]: LensCommentForum,
  [Platform.Gplus]: LensGenericPosts,
  [Platform.Instagram]: LensInstagram,
  [Platform.Linkedin]: LensLinkedin,
  [Platform.LittleRedBook]: LensLittleRedBook,
  [Platform.Media]: LensBlogMediaWebsite,
  [Platform.Pinterest]: LensPinterest,
  [Platform.Press]: LensPress,
  [Platform.Radio]: LensRadioTelevision,
  [Platform.Review]: LensReview,
  [Platform.Sinaweibo]: LensSinaweibo,
  [Platform.Television]: LensRadioTelevision,
  [Platform.Tiktok]: LensTiktok,
  [Platform.Twitch]: LensTwitch,
  [Platform.Twitter]: LensTwitter,
  [Platform.Vkontakte]: LensVkontakte,
  [Platform.Website]: LensBlogMediaWebsite,
  [Platform.Wechat]: LensWechat,
  [Platform.Youku]: LensGenericPosts,
  [Platform.Youtube]: LensYoutube,
  [Platform.Reddit]: LensReddit,
};

export class PostLensFactory<P extends Post> {
  post: P;
  static create<P extends Post>(post: P): PostLens<P> {
    if (postPlatforms[post.origin.platform]) {
      return new postPlatforms[post.origin.platform](post);
    }
    return new PostLens(post);
  }
}

interface UpdatedPostList {
  set?: string[];
  add?: string[];
  remove?: string[];
}

export interface UpdatedPostData {
  lang?: string; // @TODO
  tone?: Tone;
  keyword?: UpdatedPostList;
  geo?: {
    inferred: {
      country: Country;
    };
  };
  radar?: {
    favorite?: boolean;
    trash?: boolean;
    userUpdated?: string;
    tag?: {
      custom?: Record<string, UpdatedPostList>;
    };
  };
}

export function getUpdatedPost(post: Post, doc: UpdatedPostData) {
  return produce(post, (draft) => {
    function getUpdatedValueFromList(list: UpdatedPostList, keys: string[]) {
      const existingList: string[] = get(post, keys, []);
      if (list.set) {
        return list.set;
      } else if (list.add) {
        return uniq([...existingList, ...list.add]);
      } else if (list.remove) {
        return difference(existingList, list.remove);
      }
    }
    function updatePost(_doc, keys = []) {
      each(_doc, (value, key) => {
        const currentKeys = [...keys, key];
        if (key === "keyword" && !keys.length) {
          set(draft, currentKeys, getUpdatedValueFromList(value, currentKeys));
        } else if (isEqual(keys, ["radar", "tag", "custom"])) {
          set(draft, currentKeys, getUpdatedValueFromList(value, currentKeys));
        } else if (isObject(value)) {
          updatePost(value, currentKeys);
        } else {
          set(draft, currentKeys, value);
        }
      });
    }
    updatePost(doc);
  });
}

export function getExistingUpdatedPostData(post: Post, doc: UpdatedPostData) {
  const previousDoc: UpdatedPostData = {};
  function _getExistingUpdatedPostData(_doc, keys = []) {
    each(_doc, (value, key) => {
      if (key === "keyword" && !keys.length) {
        set(previousDoc, key, {
          set: get(post, [...keys, key]),
        });
      } else if (isEqual(keys, ["radar", "tag", "custom"])) {
        set(previousDoc, [...keys, key], {
          set: get(post, [...keys, key]),
        });
      } else if (isObject(value)) {
        _getExistingUpdatedPostData(value, [...keys, key]);
      } else {
        set(previousDoc, [...keys, key], get(post, [...keys, key]));
      }
    });
  }
  if (post) {
    if (doc) {
      _getExistingUpdatedPostData(doc);
      return previousDoc;
    } else {
      return null;
    }
  } else {
    return null;
  }
}

export interface RealtimeOptions {
  pause: boolean;
  start: AbstractDate;
  maxPosts: number;
  fetchDelay: number;
  queueDealy: number;
  isLive: boolean;
}

export function getAlcmeonReplyUrl(
  alcmeonId: Project["alcmeonCompany"],
  post: FacebookPost | TwitterPost
) {
  if (alcmeonId) {
    const postId = PostLens.isPlatform(Platform.Twitter, post)
      ? post.idStr
      : post.id;
    return `https://alcmeon.com/c/v3/#/${alcmeonId}/task-by-external-id/${postId}`;
  } else {
    return "";
  }
}
